import React, { useState } from "react";
import { PortfolioData } from "../types/portfolio";

interface ExperienceProps {
  data: PortfolioData;
}

const Experience: React.FC<ExperienceProps> = ({ data }) => {
  const [expandedId, setExpandedId] = useState<string | null>(
    data.experience[0]?.id || null,
  );

  return (
    <section id="experience" className="py-24 bg-white">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <div className="mb-16">
          <h2 className="text-4xl font-manrope font-bold text-navy mb-4">
            Professional Experience
          </h2>
          <div className="h-1 w-16 bg-teal rounded-full"></div>
        </div>

        {/* Modern Timeline */}
        <div className="space-y-4">
          {data.experience.map((exp, idx) => (
            <div
              key={exp.id}
              className="group animate-fadeInUp"
              style={{ animationDelay: `${idx * 0.1}s` }}
            >
              <button
                onClick={() =>
                  setExpandedId(expandedId === exp.id ? null : exp.id)
                }
                className="w-full text-left"
              >
                {/* Card Header */}
                <div
                  className={`p-6 rounded-lg border transition-all duration-300 cursor-pointer ${
                    expandedId === exp.id
                      ? "bg-gradient-to-r from-navy/5 to-teal/5 border-teal shadow-md"
                      : "bg-white border-gray-200 hover:border-teal hover:shadow-sm"
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    {/* Left Content */}
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-4 mb-3">
                        <h3 className="text-xl font-manrope font-bold text-navy">
                          {exp.title}
                        </h3>
                        <p className="text-sm font-semibold text-teal">
                          {exp.company}
                        </p>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:gap-6 text-sm text-slate">
                        <p className="font-medium">{exp.duration}</p>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          {exp.category}
                        </p>
                      </div>
                    </div>

                    {/* Toggle Icon */}
                    <div
                      className={`flex-shrink-0 transition-all duration-300 ${
                        expandedId === exp.id
                          ? "text-teal rotate-180"
                          : "text-navy group-hover:text-teal"
                      }`}
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedId === exp.id && (
                  <div className="bg-gradient-to-r from-navy/5 to-teal/5 border border-teal border-t-0 rounded-b-lg p-6 animate-slideIn">
                    <ul className="space-y-3">
                      {exp.responsibilities.map((resp, respIdx) => (
                        <li key={respIdx} className="flex gap-3">
                          <span className="inline-block w-1.5 h-1.5 bg-teal rounded-full mt-2 flex-shrink-0"></span>
                          <p className="text-slate leading-relaxed text-sm md:text-base">
                            {resp}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </button>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="mt-12 pt-8 border-t border-gray-200 text-center">
          <p className="text-slate text-sm leading-relaxed max-w-2xl mx-auto">
            My career at Kyndryl spans from infrastructure support to strategic
            business analysis, demonstrating a progression toward higher-impact
            analytical and process improvement roles.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Experience;
